/**
 * Ground-truth entry — the reason stage 1 exists.
 *
 * CORRECTNESS REQUIREMENT, not a preference: the model's verdict MUST NOT be reachable
 * anywhere in this subtree until the observation has been submitted. That is why the
 * verdict is not rendered, not put in a title/aria-label, and not even formatted while
 * `phase === "entry"`: the reveal markup is built only after the POST succeeds. Showing
 * the verdict first turns the whole exercise into confirmation bias and the agreement
 * number becomes worthless (визуальный контракт; правило сверки).
 *
 * The parent (Console) hides its own verdict block while this form is open, for the same
 * reason — a number already on screen refreshes the observer's memory just as well.
 */

import { useState } from "react";

import { T } from "../strings";
import type { GroundTruthInput, GroundTruthState } from "../api/types";
import { OUTCOME_COLOR_VAR, OUTCOME_LABEL, outcomeOfPair } from "../lib/agreement";
import type { Outcome } from "../api/types";
import { todayIso } from "../lib/format";
import type { Verdict } from "../lib/verdict";

const STATE_ORDER: GroundTruthState[] = [
  "standing_crop",
  "stubble",
  "bare_soil",
  "sown",
  "not_reached",
];

/** The server's rule (truth.classify_outcome), applied to the pair on screen. */
const outcomeFor = (verdict: Verdict | null, observed: GroundTruthState): Outcome => {
  if (!verdict) return "no_verdict";
  // "ВОЗМОЖНО" is an abstention: it never counts as right or wrong. "ПОД КУЛЬТУРОЙ" is
  // scored the same way, and truth.py agrees: it reads as a negative, but counting it as
  // one would mint a specificity out of a project with zero verified negative fields.
  // «Поле голое» abstains with the other non-answers on purpose. The field is bare and the
  // observation may well agree — but this is a STATE reading that declared no event, and
  // counting it as a hit would put it into the detection rate the lockbox measured
  // (13/18 = 0.72, Wilson [0.49, 0.88]), which it has no business being in.
  if (verdict.kind === "maybe" || verdict.kind === "crop" || verdict.kind === "fallow") {
    return "uncertain";
  }
  return outcomeOfPair(observed, verdict.kind === "free");
};

interface Props {
  fieldName: string;
  /** Withheld until submit. Never rendered in the entry phase — see the header comment. */
  verdict: Verdict | null;
  existingCount: number;
  runId: number | null;
  /**
   * Was the verdict actually still sealed when this form opened? Not always true: the
   * operator can press "Показать вердикт без наблюдения" first and only then open the form,
   * and recording that as blind puts a compromised row inside the go/no-go number.
   */
  blind: boolean;
  onSubmit: (input: GroundTruthInput) => Promise<boolean>;
  onClose: () => void;
}

export function GroundTruthForm({
  fieldName,
  verdict,
  existingCount,
  runId,
  blind,
  onSubmit,
  onClose,
}: Props) {
  const [phase, setPhase] = useState<"entry" | "saving" | "revealed">("entry");
  const [observedOn, setObservedOn] = useState(todayIso());
  const [observedState, setObservedState] = useState<GroundTruthState | null>(null);
  const [confidence, setConfidence] = useState<1 | 2 | 3>(3);
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);

  const submit = async (): Promise<void> => {
    if (!observedState) {
      setError(T.groundTruth.needState);
      return;
    }
    setError(null);
    setPhase("saving");
    const ok = await onSubmit({
      observed_on: observedOn,
      observed_state: observedState,
      observer_confidence: confidence,
      note: note.trim() || null,
      run_id: runId,
      // The only honest blindness signal the working order (run -> drive out -> record) can
      // produce — which is exactly why it must not be hardcoded true.
      blind_entry: blind,
    });
    setPhase(ok ? "revealed" : "entry");
    // Не голая «Ошибка» (`common.error` удалён 2026-09-05): отказ называет предмет и орган —
    // точную подпись кнопки `submit`.
    if (!ok) setError(T.groundTruth.saveFailed);
  };

  if (phase === "revealed") {
    const outcome = outcomeFor(verdict, observedState ?? "not_reached");
    return (
      <section className="k-blind k-reveal" data-testid="ground-truth-reveal">
        <div className="k-label">{T.groundTruth.saved}</div>
        {verdict ? (
          <div
            className="k-agree"
            style={{ ["--outcome-color" as string]: OUTCOME_COLOR_VAR[outcome] }}
          >
            <div className="k-label">{T.groundTruth.revealTitle}</div>
            <div
              className="k-verdict__state"
              style={{ fontSize: "1.5rem" }}
              data-testid="verdict-state"
            >
              {verdict.label}
            </div>
            {/* Это поверхность ОПЕРАТОРА, а не продукта: сюда смотрят, чтобы сверить
                наблюдение с моделью, и число здесь названо так же, как в служебных данных.
                На карточке ответа его нет — там стоит наблюдение словами. */}
            <div className="k-row">
              <span className="k-label">{T.dev.score}</span>
              <span className="k-mono" data-testid="verdict-score">
                {verdict.score ?? T.common.noData}
              </span>
            </div>
            <div className="k-caption">
              {verdict.kind === "nodata" ? verdict.reason : verdict.human}
            </div>
            <div className="k-outcome" data-testid="ground-truth-outcome">
              {OUTCOME_LABEL[outcome]}
            </div>
          </div>
        ) : (
          <p className="k-caption">{T.groundTruth.noVerdict}</p>
        )}
        <button type="button" className="k-btn" onClick={onClose}>
          {T.common.close}
        </button>
      </section>
    );
  }

  return (
    <section className="k-blind" data-testid="ground-truth-form">
      <div className="k-blind__banner">
        {blind ? T.groundTruth.blindBanner : T.groundTruth.notBlindBanner}
      </div>
      <div className="k-caption">
        {fieldName}
        {existingCount > 0 ? ` · ${T.groundTruth.existing(existingCount)}` : ""}
      </div>

      <label className="k-field">
        <span className="k-label">{T.groundTruth.observedOn}</span>
        <input
          className="k-input"
          type="date"
          value={observedOn}
          max={todayIso()}
          onChange={(event) => setObservedOn(event.target.value)}
        />
      </label>

      <div className="k-field">
        <span className="k-label">{T.groundTruth.state}</span>
        <div className="k-chips" role="group" aria-label={T.groundTruth.state}>
          {STATE_ORDER.map((state) => (
            <button
              key={state}
              type="button"
              className="k-chip"
              aria-pressed={observedState === state}
              onClick={() => setObservedState(state)}
            >
              {T.groundTruth.states[state]}
            </button>
          ))}
        </div>
      </div>

      <div className="k-field">
        <span className="k-label">{T.groundTruth.confidence}</span>
        <div className="k-chips" role="group" aria-label={T.groundTruth.confidence}>
          {([1, 2, 3] as const).map((level) => (
            <button
              key={level}
              type="button"
              className="k-chip"
              aria-pressed={confidence === level}
              onClick={() => setConfidence(level)}
            >
              {T.groundTruth.confidenceLevels[level]}
            </button>
          ))}
        </div>
      </div>

      <label className="k-field">
        <span className="k-label">{T.groundTruth.note}</span>
        <textarea
          className="k-textarea"
          value={note}
          placeholder={T.groundTruth.notePlaceholder}
          onChange={(event) => setNote(event.target.value)}
        />
      </label>

      {error && <div className="k-note k-note--error">{error}</div>}

      <div className="k-row">
        <button
          type="button"
          className="k-btn k-btn--primary"
          disabled={phase === "saving"}
          onClick={() => void submit()}
        >
          {phase === "saving" ? T.groundTruth.saving : T.groundTruth.submit}
        </button>
        <button type="button" className="k-btn k-btn--ghost" onClick={onClose}>
          {T.groundTruth.cancel}
        </button>
      </div>
    </section>
  );
}
